import { useEffect } from "react";

const SITE = "https://nickrobotic.com";

export interface SeoProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  jsonLd?: object | object[];
}

/**
 * Per-route SEO head — pure DOM, no react-helmet-async.
 *
 * Removing helmet shaves ~12KB gzip from the initial bundle and removes
 * <HelmetProvider> from the React tree, which used to add an extra context
 * layer on every render. Effects below mutate document.head directly and
 * clean up tags they own on unmount so route changes do not leak meta.
 *
 * Limitation: tags only appear after JS hydrates. Social preview crawlers
 * that don't run JS still see the sitewide og:* baked into index.html, which
 * is the right fallback. JS-executing crawlers (Googlebot) see the per-route
 * tags. This matches helmet's behaviour exactly.
 */
const OWNED_ATTR = "data-seo";

function upsertMeta(key: "name" | "property", keyValue: string, content: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${keyValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, keyValue);
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertCanonical(href: string): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

export default function Seo({
  title,
  description,
  path = "/",
  image = `${SITE}/og-image.jpg`,
  type = "website",
  noindex,
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const url = `${SITE}${path}`;
    document.title = title;

    upsertMeta("name", "description", description);
    upsertCanonical(url);
    upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:site_name", "NickRobotic");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // JSON-LD: append fresh nodes per render, remove on cleanup.
    const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const ldNodes: HTMLScriptElement[] = ldArray.map((ld) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(OWNED_ATTR, "ld");
      s.text = JSON.stringify(ld);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      ldNodes.forEach((n) => n.remove());
    };
  }, [title, description, path, image, type, noindex, JSON.stringify(jsonLd)]);

  return null;
}
